import { renderToStaticMarkup } from 'react-dom/server';
import { format, addDays } from 'date-fns';

import TooltipSeason from '../../../components/tooltip-season/tooltip-season.component';
import { COLORS } from '../../../utilities';



export function oakPhenologyAnnualOptions(chartData, address, oakName) {
  const {
    years,
    leafTimestamps,
    bloomTimestamps,
    numDamagingFreezeEvents,
    finalFreezeEventTimestamps
  } = chartData;

  const timestamps = [...leafTimestamps, ...bloomTimestamps, ...finalFreezeEventTimestamps].filter(v => v !== null);
  const min = addDays(new Date(Math.min(...timestamps)), -1).getTime();
  const max = addDays(new Date(Math.max(...timestamps)), 1).getTime();
  const longTermAverage = finalFreezeEventTimestamps.reduce((acc, v) => acc + v, 0) / finalFreezeEventTimestamps.length;

  return {
    chart: {
      marginRight: 20,
      spacingBottom: 17
    },
    title: {
      text: `Last Spring Freeze and ${oakName} Phenology Timing`,
      margin: 18
    },
    subtitle: {
      text: `Location: ${address}`,
      y: 34
    },
    series: [
      {
        data: leafTimestamps,
        name: 'Breaking Leaf Buds',
        color: COLORS.green
      },{
        data: bloomTimestamps,
        name: 'Open Flowers',
        color: COLORS.lightGray
      },{
        data: finalFreezeEventTimestamps,
        name: 'Last Spring Freeze',
        type: 'area',
        color: `rgba(${COLORS.lightBlueRgb}, 0.3)`,
        fillOpacity: 0.3,
        zIndex: 0,
        marker: { enabled: false }
      }
    ],
    xAxis: {
      categories: years,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Year',
      }
    },
    yAxis: [
      {
        min,
        max,
        title: {
          text: 'Date',
        },
        labels: {
          format: '{value:%m-%d}',
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        },
        plotLines: [{
          value: longTermAverage,
          color: COLORS.lightBlue,
          label: {
            text: 'Avg. Last Spring Freeze',
            style: {
              color: COLORS.darkBlue
            }
          }
        }]
      },
    ],
    legend: {
      floating: true,
      y: 21
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';

        const date = this.points[0].x;
        const pointIndex = this.points[0].point.index;
        const isInRiskBand = numDamagingFreezeEvents[pointIndex] > 0;

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={isInRiskBand ? COLORS.darkRed : COLORS.lightBlue} />

            {isInRiskBand ?
              <div className='mc-season warning'>Potential damage</div>
              :
              ''
            }

            {this.points.map(point => (
              <div key={point.y} className='mc-container' style={{ justifyContent: 'space-between' }}>
                <div className='mc-units' style={{ display: 'flex', gap: '3px' }}>
                  <div style={{ alignSelf: 'center', height: '6px', width: '6px', backgroundColor: point.series.color, opacity: point.series.opacity }} />
                  <span>{point.series.name}: </span>
                </div>
                <div className='mc-number'>{format(new Date(point.y), 'MM-dd')}</div>
              </div>
            ))}
          </div>
        );
      },
    }
  };
};

export function springFreezeAnnualOptions(chartData, address, oakName) {
  const {
    years,
    numDamagingFreezeEvents
  } = chartData;

  const min = 0;
  const max = Math.max(...numDamagingFreezeEvents) * 1.05;

  return {
    chart: {
      marginRight: 20,
      spacingBottom: 17
    },
    title: {
      text: `Damaging Spring Freeze Events for ${oakName}`,
      margin: 18
    },
    subtitle: {
      text: `Location: ${address}`,
      y: 34
    },
    series: [
      {
        data: numDamagingFreezeEvents,
        name: 'Damaging Freeze Events',
        color: `rgba(${COLORS.darkRedRgb},0.6)`,
        type: 'column',
        borderWidth: 0,
        groupPadding: 0
      }
    ],
    xAxis: {
      categories: years,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Year',
      }
    },
    yAxis: [
      {
        min,
        max,
        title: {
          text: 'Number of Events',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        }
      }
    ],
    legend: { enabled: false },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';

        const date = this.points[0].x;
        const name = this.points[0].point.series.name;
        const val = this.points[0].point.y;

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={val > 0 ? COLORS.darkRed : COLORS.lightBlue} />

            <div className='mc-container' style={{ justifyContent: 'space-between' }}>
              <div className='mc-units'>{name}: </div>
              <div className='mc-number'>{val}</div>
            </div>
          </div>
        );
      },
    }
  };
}

export function dailyMinimumTemperaturesOptions(chartData, address, tabInfoContextState) {
  const {
    tempThreshold,
    years,
    mints,
    leafTimestamps,
    bloomTimestamps
  } = chartData;

  let selectedYear, series, dates, phenologies;
  try {
    selectedYear = parseInt(tabInfoContextState.tabsSharedState.springFreezeChartYearSelector.value);
    const selectedYearIndex = years.findIndex(y => y === selectedYear);
    
    let data;
    ({ data, dates } = mints[selectedYearIndex].reduce((acc, [date, temp]) => {
      acc.data.push(temp);
      acc.dates.push(date);
      return acc;
    }, { data: [], dates: [] }))
    
    const leafTimestamp = leafTimestamps[selectedYearIndex];
    let leafoutIndex = null;
    if (leafTimestamp !== null) {
      const leafoutDate = format(new Date(leafTimestamp), 'MM-dd');
      leafoutIndex = dates.findIndex(d => d === leafoutDate);
    }
    
    const bloomTimestamp = bloomTimestamps[selectedYearIndex];
    let budBreakIndex = null;
    if (bloomTimestamp !== null) {
      const budBreakDate = format(new Date(bloomTimestamp), 'MM-dd');
      budBreakIndex = dates.findIndex(d => d === budBreakDate);
    }
    
    phenologies = [{
      value: leafoutIndex,
      color: COLORS.darkGreen,
      label: {
        text: 'Breaking Leaf Buds',
        style: {
          color: COLORS.darkGreen
        },
        y: 0
      }
    },{
      value: budBreakIndex,
      color: COLORS.lightGray,
      label: {
        text: 'Open Flowers',
        style: {
          color: COLORS.darkGray
        },
        y: 0
      }
    }];

    series = [{
      data: data.map((d, i) => ({
        y: d,
        color: (leafoutIndex !== null && leafoutIndex <= i && d <= tempThreshold[0]) ? COLORS.darkRed : COLORS.lightBlue
      })),
      name: 'Minimum Temperature'
    }];
  } catch {
    series = [];
    phenologies = [];
  }

  return {
    chart: {
      marginRight: 20,
      spacingBottom: 17
    },
    title: {
      text: selectedYear ? `Daily Minimum Temperatures in ${selectedYear - 1}-${selectedYear} Season` : 'Daily Minimum Temperatures',
      margin: 18
    },
    subtitle: {
      text: `Location: ${address}`,
      y: 34
    },
    series,
    xAxis: {
      categories: dates,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Date',
      },
      plotLines: phenologies
    },
    yAxis: [
      {
        startOnTick: false,
        endOnTick: false,
        tickAmount: 5,
        gridLineWidth: 0,
        title: {
          text: 'Temperature (⁰F)',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        },
        plotLines: [{
          value: tempThreshold[0],
          color: 'rgb(200,200,200)',
          label: {
            text: `Threshold: ${tempThreshold[0]}°F`,
            align: 'right',
            style: {
              color: 'rgb(150,150,150)',
            }
          }
        }]
      },
    ],
    legend: { enabled: false },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';

        const date = this.points[0].x;
        const color = this.points[0].color;

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={color} />

            {this.points.map(point => (
              <div key={point.y} className='mc-container' style={{ justifyContent: 'space-between' }}>
                <div className='mc-number'>{Math.round(point.y * 10) / 10} ⁰F</div>
              </div>
            ))}
          </div>
        );
      },
    }
  };
}